/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { SHIFTIQ_API, getAuthHeaders, isAbortError } from "../../api/client";
import { LoadingPulse } from "../../ui";
import { procureOrdersQueryFromSearch } from "../../routing/paths";
import { supyOrderUrl } from "./orderIssueUx";
import {
  formatProcureEuro,
  procureiqApiError,
  procureiqDisplayProductName,
  procureiqDisplaySupplierName,
} from "./helpers";

/**
 * Bare order view for new-tab deep links (`/procure-order?store=&brand=&order=`).
 * No sidebar / Procure Orders chrome — Close tab + optional Show on Supy only.
 */
export function ProcureOrderViewPage({ lang }) {
  const t = (en, gr) => (lang === "gr" ? gr : en);
  const query = useMemo(
    () =>
      procureOrdersQueryFromSearch(
        typeof window !== "undefined" ? window.location.search : ""
      ),
    []
  );
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const orderKey = String(query.orderKey || "").trim();
    const locationId = query.locationId;
    if (!orderKey || locationId == null) {
      setLoading(false);
      setError(
        t(
          "Missing order or store in the link.",
          "Λείπει παραγγελία ή κατάστημα από τον σύνδεσμο."
        )
      );
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const qs = new URLSearchParams({
          location_id: String(locationId),
          brand_id: String(query.brandId != null ? query.brandId : 15),
          from_date: daysAgo(45),
          to_date: todayIso(),
        });
        const res = await fetch(
          `${SHIFTIQ_API}/api/procureiq/receipt-review/${encodeURIComponent(orderKey)}?${qs}`,
          { headers: getAuthHeaders() }
        );
        if (!res.ok) throw new Error(await procureiqApiError(res, "Load order failed"));
        const json = await res.json();
        if (cancelled) return;
        setDetail(json);
      } catch (err) {
        if (cancelled || isAbortError(err)) return;
        setError(err?.message || "Error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [query.orderKey, query.locationId, query.brandId]);

  const showOnSupyHref = detail?.supy_order_id
    ? supyOrderUrl(detail.supy_order_id)
    : null;
  const lines = Array.isArray(detail?.lines) ? detail.lines : [];

  function closeTab() {
    window.close();
    window.setTimeout(() => {
      if (window.history.length > 1) window.history.back();
      else window.location.href = "/procure-orders";
    }, 150);
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--bg, #f4f6f5)",
        color: "var(--text-primary)",
        padding: "16px 14px 32px",
        boxSizing: "border-box",
      }}
    >
      <div
        className="card-box"
        style={{
          maxWidth: 720,
          margin: "0 auto",
          position: "relative",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 10,
          }}
        >
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 18, lineHeight: 1.25 }}>
              {loading
                ? t("Order", "Παραγγελία")
                : procureiqDisplaySupplierName(detail?.supplier_name || "Order")}
            </div>
            {!loading && detail ? (
              <div
                style={{
                  fontSize: 12,
                  color: "var(--text-muted)",
                  marginTop: 6,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  alignItems: "center",
                }}
              >
                {(detail.supy_order_number || detail.supy_order_id) && (
                  <span>
                    {t("Supy", "Supy")}{" "}
                    <span style={{ fontWeight: 700, color: "var(--text-primary)" }}>
                      {detail.supy_order_number ||
                        String(detail.supy_order_id).slice(0, 8)}
                    </span>
                  </span>
                )}
                <span>
                  {t("Status", "Κατάσταση")} {detail.status || "—"}
                </span>
                <span>
                  {t("Expected", "Αναμενόμενο")}{" "}
                  {detail.expected_delivery_date || "—"}
                </span>
              </div>
            ) : null}
          </div>
          <div style={{ display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap" }}>
            {showOnSupyHref ? (
              <a
                href={showOnSupyHref}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-base btn-outline btn-small"
              >
                {t("Show on Supy", "Εμφάνιση στο Supy")} ↗
              </a>
            ) : null}
            <button
              type="button"
              className="btn-base btn-outline btn-small"
              onClick={closeTab}
              aria-label={t("Close", "Κλείσιμο")}
            >
              {t("Close", "Κλείσιμο")}
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: "48px 12px", textAlign: "center" }}>
            <LoadingPulse label={t("Loading order…", "Φόρτωση παραγγελίας…")} />
          </div>
        ) : error ? (
          <div
            style={{
              padding: 14,
              borderRadius: 12,
              background: "#FEF0ED",
              border: "1px solid #FACFC8",
              color: "var(--danger, #b91c1c)",
              fontWeight: 700,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        ) : (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 8,
                padding: "10px 12px",
                background: "var(--surface2)",
                borderRadius: 12,
                border: "1px solid var(--border)",
                marginBottom: 14,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "var(--text-muted)",
                    textTransform: "uppercase",
                  }}
                >
                  {t("Net", "Καθαρή")}
                </div>
                <div style={{ fontWeight: 800, fontSize: 14, marginTop: 2 }}>
                  {detail.total_ordered != null
                    ? formatProcureEuro(detail.total_ordered)
                    : "—"}
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "var(--text-muted)",
                    textTransform: "uppercase",
                  }}
                >
                  {detail.tax_rate_pct != null
                    ? t(`VAT ${detail.tax_rate_pct}%`, `ΦΠΑ ${detail.tax_rate_pct}%`)
                    : t("VAT", "ΦΠΑ")}
                </div>
                <div style={{ fontWeight: 800, fontSize: 14, marginTop: 2 }}>
                  {detail.applied_tax != null
                    ? formatProcureEuro(detail.applied_tax)
                    : "—"}
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "var(--text-muted)",
                    textTransform: "uppercase",
                  }}
                >
                  {t("Gross", "Με ΦΠΑ")}
                </div>
                <div style={{ fontWeight: 800, fontSize: 14, marginTop: 2 }}>
                  {detail.ordered_total != null
                    ? formatProcureEuro(detail.ordered_total)
                    : "—"}
                </div>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "2fr 0.7fr 0.9fr 0.9fr",
                gap: 8,
                marginBottom: 6,
                fontSize: 10,
                fontWeight: 700,
                color: "var(--text-muted)",
                textTransform: "uppercase",
              }}
            >
              <div>{t("Item", "Είδος")}</div>
              <div>{t("Qty", "Ποσ.")}</div>
              <div>{t("Net unit €", "Καθ. τιμή €")}</div>
              <div style={{ textAlign: "right" }}>{t("Net line", "Καθ. γραμμή")}</div>
            </div>
            {!lines.length ? (
              <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
                {t("No lines on this order.", "Δεν υπάρχουν γραμμές σε αυτή την παραγγελία.")}
              </p>
            ) : (
              lines.map((line, idx) => {
                const qty = Number(line.ordered_qty ?? line.qty);
                const price = Number(line.ordered_unit_price ?? line.unit_price);
                const lineTotal =
                  Number.isFinite(qty) && Number.isFinite(price) ? qty * price : null;
                return (
                  <div
                    key={`${line.item_id || "l"}-${idx}`}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "2fr 0.7fr 0.9fr 0.9fr",
                      gap: 8,
                      padding: "8px 0",
                      borderBottom: "1px solid var(--border, #e5e7eb)",
                      fontSize: 13,
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>
                      {procureiqDisplayProductName(line.product_name)}
                      {line.item_code ? (
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 500,
                            color: "var(--text-muted)",
                            marginTop: 2,
                          }}
                        >
                          {line.item_code}
                        </div>
                      ) : null}
                    </div>
                    <div style={{ alignSelf: "center" }}>
                      {Number.isFinite(qty) ? qty : "—"}
                    </div>
                    <div style={{ alignSelf: "center" }}>
                      {Number.isFinite(price) ? formatProcureEuro(price) : "—"}
                    </div>
                    <div
                      style={{
                        alignSelf: "center",
                        textAlign: "right",
                        fontWeight: 700,
                      }}
                    >
                      {lineTotal != null ? formatProcureEuro(lineTotal) : "—"}
                    </div>
                  </div>
                );
              })
            )}
          </>
        )}
      </div>
    </div>
  );
}

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function todayIso() {
  return isoDate(new Date());
}

function daysAgo(n) {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() - n);
  return isoDate(d);
}
